/* sections/collection.liquid — sub-collection tab strip.

   `.custom-filter-tabs` is a horizontal scroller and every tab navigates with a full
   page load, so the selected tab has to be centred on each load, otherwise the later
   tabs land selected but scrolled out of sight and the strip looks inactive.

   Loaded synchronously right after the strip markup rather than deferred: the strip is
   above the fold, so positioning it after first paint shows up as the row jumping. */
(() => {
  const strip = document.querySelector('[data-collection-tabs]');
  if (!strip) return;

  const GUTTER_PX = 1;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Set once the visitor takes over the strip, so a late re-centre (fonts, resize)
  // never yanks the row out from under them.
  let userTookOver = false;
  ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => {
    strip.addEventListener(type, () => { userTookOver = true; }, { passive: true, once: true });
  });

  const activeTab = () => strip.querySelector('.tab-button.current_now');
  const isSale = strip.dataset.collectionHandle === 'sale';

  /* Sale isn't a real collection per tab, so tabs can't jump to e.g. /collections/bundles —
     that abandons Sale entirely. Instead every product card in the grid carries
     data-tab-categories (set in sections/collection.liquid from real Shopify collection
     membership, not tags — Bestsellers/Bundles/Merch aren't taggable), and clicking a
     Sale tab just shows/hides cards in place. The URL never changes.

     Looked up lazily inside filterSaleGrid rather than cached here: this script runs
     synchronously right after the strip markup (see the note above), which is before
     the product grid further down the page has even been parsed yet. */
  const filterSaleGrid = (tabHandle) => {
    const productsRoot = document.querySelector('[data-collection-products]');
    if (!productsRoot) return;
    const productsCountEl = document.querySelector('[data-products-count]');

    let visibleCount = 0;
    productsRoot.querySelectorAll('[data-tab-categories]').forEach((card) => {
      const categories = card.dataset.tabCategories.split(' ');
      const visible = tabHandle === 'all' || categories.includes(tabHandle);
      card.style.display = visible ? '' : 'none';
      if (visible) visibleCount += 1;
    });

    if (productsCountEl) {
      productsCountEl.textContent = productsCountEl.textContent.replace(/\d+/, visibleCount);
    }

    let emptyMessage = productsRoot.querySelector('[data-tab-empty]');
    if (visibleCount === 0) {
      if (!emptyMessage) {
        // Mirrors the theme's own server-rendered empty state (.collection--no-results > .no-matches)
        // so a tab with nothing to show looks the same as a genuinely empty collection.
        emptyMessage = document.createElement('div');
        emptyMessage.setAttribute('data-tab-empty', '');
        emptyMessage.className = 'collection--no-results';
        const text = document.createElement('p');
        text.className = 'no-matches';
        text.textContent = productsRoot.dataset.emptyMessage || '';
        emptyMessage.appendChild(text);
        productsRoot.appendChild(emptyMessage);
      }
      emptyMessage.style.display = '';
    } else if (emptyMessage) {
      emptyMessage.style.display = 'none';
    }
  };

  /* On Default collection, each tab's banner naturally differs because it's a real,
     separate page. On Sale it's one page/one <picture>, so switching tabs has to swap
     that same banner's sources in place. The resolved image per tab (Market banner
     override block if one matches, else that collection's own metafield banner — same
     priority sections/collection-banner.liquid uses for the page's own banner) is
     precomputed server-side onto the banner element itself as
     data-tab-banner-{handle}-desktop/-mobile, since collection-banner.liquid is the
     only section with access to those override blocks. A tab with neither just leaves
     whatever is currently showing. */
  let originalBanner = null;
  const updateSaleBanner = (tab) => {
    const bannerContainer = document.querySelector('[data-banner-image]');
    if (!bannerContainer) return;
    const desktopSource = bannerContainer.querySelector('source[media="(min-width: 768px)"]');
    const mobileSource = bannerContainer.querySelector('source[media="(max-width: 767px)"]');
    const img = bannerContainer.querySelector('img');
    if (!img) return;

    if (!originalBanner) {
      originalBanner = {
        desktopSrcset: desktopSource ? desktopSource.srcset : '',
        mobileSrcset: mobileSource ? mobileSource.srcset : '',
        imgSrc: img.src,
        imgSrcset: img.srcset,
      };
    }

    const handle = tab.dataset.tabHandle;
    const bannerDesktop = bannerContainer.dataset[`tabBanner${handle[0].toUpperCase()}${handle.slice(1)}Desktop`];
    const bannerMobile = bannerContainer.dataset[`tabBanner${handle[0].toUpperCase()}${handle.slice(1)}Mobile`];
    if (handle === 'all' || (!bannerDesktop && !bannerMobile)) {
      if (desktopSource) desktopSource.srcset = originalBanner.desktopSrcset;
      if (mobileSource) mobileSource.srcset = originalBanner.mobileSrcset;
      img.src = originalBanner.imgSrc;
      img.srcset = originalBanner.imgSrcset;
      return;
    }

    const fallback = bannerDesktop || bannerMobile;
    if (desktopSource && bannerDesktop) desktopSource.srcset = bannerDesktop;
    if (mobileSource && bannerMobile) mobileSource.srcset = bannerMobile;
    img.src = fallback;
    img.srcset = fallback;
  };

  /* Measured with getBoundingClientRect + scrollBy rather than offsetLeft/scrollLeft:
     `.tabs` is not a positioned ancestor, so offsetLeft resolves against a far ancestor,
     and scrollLeft is negative in the RTL (/ar) locale. Rect deltas work in both
     directions, and scrollBy clamps itself at the strip's two ends. */
  const centreTab = (tab, animate) => {
    if (!tab || strip.scrollWidth - strip.clientWidth <= GUTTER_PX) return;

    const stripBox = strip.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const delta = (tabBox.left + tabBox.width / 2) - (stripBox.left + stripBox.width / 2);
    if (Math.abs(delta) < GUTTER_PX) return;

    strip.scrollBy({
      left: delta,
      behavior: animate && !reducedMotion.matches ? 'smooth' : 'auto',
    });
  };

  // Instant: this runs before the strip is painted, so there is nothing to animate from.
  centreTab(activeTab(), false);

  // Tab widths change when the display font swaps in, which moves the centre.
  // Animated because this lands after first paint.
  if (document.fonts) {
    document.fonts.ready.then(() => {
      if (!userTookOver) centreTab(activeTab(), true);
    });
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (!userTookOver) centreTab(activeTab(), true);
    }, 150);
  }, { passive: true });

  strip.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-tab-handle]');
    if (!tab || !strip.contains(tab)) return;

    strip.querySelectorAll('.tab-button.current_now').forEach((previous) => {
      previous.classList.remove('current_now');
      previous.removeAttribute('aria-current');
    });
    tab.classList.add('current_now');
    tab.setAttribute('aria-current', 'true');
    centreTab(tab, true);

    if (isSale) {
      filterSaleGrid(tab.dataset.tabHandle);
      updateSaleBanner(tab);
      return;
    }

    // Keep whatever locale prefix the current URL carries (e.g. /ar/collections/…).
    const path = window.location.pathname;
    const collectionsIndex = path.indexOf('/collections/');
    const prefix = collectionsIndex === -1 ? '' : path.substring(0, collectionsIndex);
    // The store translates collection handles per locale, so the actual URL needs
    // tabNavHandle (e.g. "شعر" under /ar) — tabHandle is the English handle used
    // everywhere else on this page (Sale filtering/banners) and would 404 here.
    window.location.href = `${prefix}/collections/${tab.dataset.tabNavHandle || tab.dataset.tabHandle}`;
  });
})();
